/* ============================================================================
   Fragrance Hut — Premium Quick View Modal
   Fetches product data via Shopify AJAX API & renders a luxury popup
============================================================================ */

(function () {
  'use strict';

  /* ── Modal Singleton ── */
  let modalEl = null;
  let overlayEl = null;
  let isOpen = false;
  let currentHandle = null;

  function getOrCreateModal() {
    if (modalEl) return { modal: modalEl, overlay: overlayEl };

    // Overlay
    overlayEl = document.createElement('div');
    overlayEl.className = 'qv-overlay';
    overlayEl.addEventListener('click', closeModal);

    // Modal
    modalEl = document.createElement('div');
    modalEl.className = 'qv-modal';
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.setAttribute('aria-label', 'Quick View');
    modalEl.innerHTML = `
      <button class="qv-modal__close" aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <div class="qv-modal__body">
        <div class="qv-modal__loader">
          <div class="qv-spinner"></div>
        </div>
      </div>
    `;

    modalEl.querySelector('.qv-modal__close').addEventListener('click', closeModal);

    document.body.appendChild(overlayEl);
    document.body.appendChild(modalEl);

    return { modal: modalEl, overlay: overlayEl };
  }

  /* ── Open ── */
  async function openModal(handle, productUrl) {
    if (isOpen && currentHandle === handle) return;
    currentHandle = handle;

    const { modal, overlay } = getOrCreateModal();
    const body = modal.querySelector('.qv-modal__body');

    // Show loader
    body.innerHTML = `<div class="qv-modal__loader"><div class="qv-spinner"></div></div>`;

    // Show
    document.body.classList.add('qv-body-locked');
    overlay.classList.add('qv-overlay--visible');
    modal.classList.add('qv-modal--visible');
    isOpen = true;

    try {
      const res = await fetch(`${window.Shopify.routes.root}products/${handle}.js`);
      if (!res.ok) throw new Error('Product not found');
      const product = await res.json();
      renderProduct(body, product, productUrl);
    } catch (err) {
      body.innerHTML = `
        <div class="qv-modal__error">
          <p>Unable to load product.</p>
          <a href="${productUrl}" class="qv-btn qv-btn--primary">View Product Page</a>
        </div>
      `;
    }
  }

  /* ── Close ── */
  function closeModal() {
    if (!isOpen) return;
    modalEl.classList.remove('qv-modal--visible');
    overlayEl.classList.remove('qv-overlay--visible');
    document.body.classList.remove('qv-body-locked');
    isOpen = false;
    currentHandle = null;
  }

  /* ── ESC key ── */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) closeModal();
  });

  /* ── Render Product ── */
  function renderProduct(container, product, productUrl) {
    const variant = product.variants.find(v => v.available) || product.variants[0];
    const hasMultipleVariants = product.variants.length > 1;
    const images = product.images || [];
    const featuredImage = product.featured_image || images[0] || '';

    // Format price
    function formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    // Compare at price
    let priceHTML = `<span class="qv-price__current">${formatMoney(variant.price)}</span>`;
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceHTML = `
        <span class="qv-price__current qv-price__sale">${formatMoney(variant.price)}</span>
        <span class="qv-price__compare">${formatMoney(variant.compare_at_price)}</span>
        <span class="qv-price__badge">SALE</span>
      `;
    }

    // Image gallery thumbnails (max 5)
    const galleryImages = images.slice(0, 5);
    let thumbnailsHTML = '';
    if (galleryImages.length > 1) {
      thumbnailsHTML = `
        <div class="qv-gallery__thumbs">
          ${galleryImages.map((img, i) => `
            <button class="qv-gallery__thumb ${i === 0 ? 'qv-gallery__thumb--active' : ''}" data-index="${i}">
              <img src="${getSizedImageUrl(img, '100x100')}" alt="" loading="lazy">
            </button>
          `).join('')}
        </div>
      `;
    }

    // Variant options — show even for single-variant products so customers see the size
    let variantOptionsHTML = '';
    if (product.options && product.options.length > 0) {
      product.options.forEach((option, optIndex) => {
        // Extract option name: handle string ("Size") or object ({name: "Size", ...})
        const optionName = (typeof option === 'object' && option !== null)
          ? (option.name || option)
          : option;

        // Collect unique values for this option from all variants
        const values = [...new Set(product.variants.map(v => v.options[optIndex]))];

        // Skip the "Default Title" pseudo-option (products with no real variants)
        if (optionName === 'Title' && values.length === 1 && values[0] === 'Default Title') return;
        if (values.length === 1 && values[0] === 'Default Title') return;

        variantOptionsHTML += `
          <div class="qv-option">
            <label class="qv-option__label">${escapeHtml(String(optionName))}</label>
            <div class="qv-option__values">
              ${values.map(val => {
                const isSelected = variant.options[optIndex] === val;
                return `<button class="qv-option__btn ${isSelected ? 'qv-option__btn--selected' : ''}" 
                  data-option-index="${optIndex}" data-value="${escapeHtml(val)}">${escapeHtml(val)}</button>`;
              }).join('')}
            </div>
          </div>
        `;
      });
    }

    // Description (truncated)
    let descriptionHTML = '';
    if (product.description) {
      // Strip HTML tags for clean display, then truncate
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = product.description;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      const truncated = plainText.length > 200 ? plainText.substring(0, 200) + '…' : plainText;
      descriptionHTML = `<p class="qv-description">${truncated}</p>`;
    }

    container.innerHTML = `
      <div class="qv-product">
        <div class="qv-product__gallery">
          <div class="qv-gallery__main">
            <img src="${getSizedImageUrl(featuredImage, '600x600')}" alt="${escapeHtml(product.title)}" class="qv-gallery__image" id="qv-main-image">
          </div>
          ${thumbnailsHTML}
        </div>
        <div class="qv-product__info">
          ${product.vendor ? `<div class="qv-vendor">${escapeHtml(product.vendor)}</div>` : ''}
          <h2 class="qv-title">${escapeHtml(product.title)}</h2>
          <div class="qv-price">${priceHTML}</div>
          <div class="qv-divider"></div>
          ${descriptionHTML}
          ${variantOptionsHTML}
          <div class="qv-actions">
            <div class="qv-quantity">
              <button class="qv-quantity__btn qv-quantity__minus" aria-label="Decrease quantity">−</button>
              <input type="number" class="qv-quantity__input" value="1" min="1" max="99" id="qv-qty">
              <button class="qv-quantity__btn qv-quantity__plus" aria-label="Increase quantity">+</button>
            </div>
            <button class="qv-btn qv-btn--primary qv-add-to-cart" 
              data-variant-id="${variant.id}"
              ${!variant.available ? 'disabled' : ''}>
              ${variant.available ? 'Add to Bag' : 'Sold Out'}
            </button>
          </div>
          <a href="${productUrl}" class="qv-view-full">View Full Details →</a>
        </div>
      </div>
    `;

    // Bind events
    bindGalleryEvents(container, galleryImages);
    bindQuantityEvents(container);
    bindVariantEvents(container, product);
    bindAddToCart(container);
  }

  /* ── Image Gallery ── */
  function bindGalleryEvents(container, images) {
    const thumbs = container.querySelectorAll('.qv-gallery__thumb');
    const mainImg = container.querySelector('#qv-main-image');
    if (!thumbs.length || !mainImg) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', function () {
        const idx = parseInt(this.dataset.index);
        mainImg.src = getSizedImageUrl(images[idx], '600x600');
        thumbs.forEach(t => t.classList.remove('qv-gallery__thumb--active'));
        this.classList.add('qv-gallery__thumb--active');
      });
    });
  }

  /* ── Quantity ── */
  function bindQuantityEvents(container) {
    const input = container.querySelector('#qv-qty');
    const minus = container.querySelector('.qv-quantity__minus');
    const plus = container.querySelector('.qv-quantity__plus');

    if (!input) return;

    minus?.addEventListener('click', () => {
      const val = parseInt(input.value) || 1;
      if (val > 1) input.value = val - 1;
    });

    plus?.addEventListener('click', () => {
      const val = parseInt(input.value) || 1;
      if (val < 99) input.value = val + 1;
    });
  }

  /* ── Variant Selection ── */
  function bindVariantEvents(container, product) {
    const optionBtns = container.querySelectorAll('.qv-option__btn');
    if (!optionBtns.length) return;

    // Track selected options
    const selectedOptions = [...(product.variants.find(v => v.available) || product.variants[0]).options];

    optionBtns.forEach(btn => {
      btn.addEventListener('click', function () {
        const optIdx = parseInt(this.dataset.optionIndex);
        const val = this.dataset.value;
        selectedOptions[optIdx] = val;

        // Update UI
        const siblings = this.parentElement.querySelectorAll('.qv-option__btn');
        siblings.forEach(s => s.classList.remove('qv-option__btn--selected'));
        this.classList.add('qv-option__btn--selected');

        // Find matching variant
        const matchingVariant = product.variants.find(v =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );

        if (matchingVariant) {
          // Update price
          const priceEl = container.querySelector('.qv-price');
          let priceHTML = `<span class="qv-price__current">${formatMoney(matchingVariant.price)}</span>`;
          if (matchingVariant.compare_at_price && matchingVariant.compare_at_price > matchingVariant.price) {
            priceHTML = `
              <span class="qv-price__current qv-price__sale">${formatMoney(matchingVariant.price)}</span>
              <span class="qv-price__compare">${formatMoney(matchingVariant.compare_at_price)}</span>
              <span class="qv-price__badge">SALE</span>
            `;
          }
          priceEl.innerHTML = priceHTML;

          // Update image if variant has one
          if (matchingVariant.featured_image) {
            const mainImg = container.querySelector('#qv-main-image');
            if (mainImg) mainImg.src = getSizedImageUrl(matchingVariant.featured_image.src, '600x600');
          }

          // Update add to cart
          const addBtn = container.querySelector('.qv-add-to-cart');
          addBtn.dataset.variantId = matchingVariant.id;
          addBtn.disabled = !matchingVariant.available;
          addBtn.textContent = matchingVariant.available ? 'Add to Bag' : 'Sold Out';
        }
      });
    });

    function formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ── Add to Cart ── */
  function bindAddToCart(container) {
    const addBtn = container.querySelector('.qv-add-to-cart');
    if (!addBtn) return;

    addBtn.addEventListener('click', async function () {
      const variantId = this.dataset.variantId;
      const qty = parseInt(container.querySelector('#qv-qty')?.value) || 1;
      const originalText = this.textContent;

      this.disabled = true;
      this.textContent = 'Adding…';
      this.classList.add('qv-btn--loading');

      try {
        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: qty }] })
        });

        if (!res.ok) throw new Error('Failed to add');

        this.textContent = '✓ Added!';
        this.classList.remove('qv-btn--loading');
        this.classList.add('qv-btn--success');

        // Update cart count in header
        updateCartCount();

        setTimeout(() => {
          this.textContent = originalText;
          this.disabled = false;
          this.classList.remove('qv-btn--success');
          closeModal();
        }, 1200);

      } catch (err) {
        this.textContent = 'Error — Try Again';
        this.disabled = false;
        this.classList.remove('qv-btn--loading');
        setTimeout(() => { this.textContent = originalText; }, 2000);
      }
    });
  }

  /* ── Update Header Cart Count ── */
  async function updateCartCount() {
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await res.json();
      // Theme uses various cart count selectors
      document.querySelectorAll('.cart-count, [data-cart-count], .header__cart-count').forEach(el => {
        el.textContent = cart.item_count;
      });
      // Dispatch event for theme to pick up
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) { /* silent */ }
  }

  /* ── Helpers ── */
  function getSizedImageUrl(src, size) {
    if (!src) return '';
    if (typeof src === 'object' && src.src) src = src.src;
    // Insert size before extension
    return src.replace(/\.(jpg|jpeg|png|gif|webp)/, `_${size}.$1`);
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ── Global Click Delegation ── */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-quick-view]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.productHandle;
    const url = btn.dataset.productUrl || `/products/${handle}`;
    if (handle) openModal(handle, url);
  });

})();
